/**
 * Applies a mask to a phone number, formatting it as (XX) XXXXX-XXXX or (XX) XXXX-XXXX.
 * @param {string} value - The unformatted phone number.
 * @returns {string} The formatted phone number.
 */
export const maskPhone = (value: string): string => {
	value = value.replace(/\D/g, '');
	if (!value) return '';

	// Handle backspace/delete case where user might be deleting the "-"
	if (value.length <= 2) {
		return `(${value.slice(0, 2)}`;
	} else if (value.length <= 6) {
		return `(${value.slice(0, 2)}) ${value.slice(2, 6)}`;
	} else if (value.length <= 10) {
		return `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(
			6,
			10,
		)}`;
	} else {
		return `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(
			7,
			11,
		)}`;
	}
};

/**
 * Applies a mask to a CPF (Brazilian Individual Taxpayer Registry) number.
 * Formats it as XXX.XXX.XXX-XX.
 * @param {string} value - The unformatted CPF number.
 * @returns {string} The formatted CPF.
 */
export const maskCPF = (value: string): string => {
	value = value.replace(/\D/g, '');

	return value
		.replace(/^(\d{3})(\d)/, '$1.$2')
		.replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
		.replace(/\.(\d{3})(\d)/, '.$1-$2');
};

/**
 * Applies a mask to a recovery code, formatting it as XXX-XXX.
 * @param {string} value - The unformatted recovery code.
 * @returns {string} The formatted recovery code.
 */
export const maskRecoveryCode = (value: string): string => {
	value = value.replace(/\D/g, '');
	return value.replace(/^(\d{3})(\d{0,3})/, '$1-$2').slice(0, 7);
};

export const maskCNPJ = (value: string): string => {
	value = removeMask(value);
	value = value.slice(0, 14);

	if (value.length <= 2) return value;
	if (value.length <= 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
	if (value.length <= 8)
		return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(5)}`;
	if (value.length <= 12)
		return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
			5,
			8,
		)}/${value.slice(8)}`;
	return `${value.slice(0, 2)}.${value.slice(2, 5)}.${value.slice(
		5,
		8,
	)}/${value.slice(8, 12)}-${value.slice(12)}`;
};

export const maskCrp = (rawCrp: string): string => {
	const digits = rawCrp.replace(/\D/g, '');

	if (digits.length <= 2) return digits;
	return `${digits.slice(0, 2)}/${digits.slice(2, 7)}`;
};

export const removeMask = (value: string): string => {
	return value.replace(/\D/g, '');
};

export const maskPostalCode = (value: string) => {
	let numbers = value.replace(/\D/g, '');

	if (numbers.length > 5) {
		return numbers.substring(0, 5) + '-' + numbers.substring(5, 8);
	}
	return numbers;
};

export const maskDate = (value: string): string => {
	// Handle ISO date string input (default value case)
	if (value.includes('T') && value.includes('-')) {
		const date = new Date(value);
		if (!isNaN(date.getTime())) {
			// Convert to DDMMYYYY format that the rest of the function expects
			const day = String(date.getDate()).padStart(2, '0');
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const year = String(date.getFullYear());
			value = day + month + year;
		}
	}

	// Remove all non-digit characters
	let numbers = value.replace(/\D/g, '');

	// Handle empty case
	if (!numbers) return '';

	// Split into day, month, year components for validation
	let day = numbers.substring(0, 2);
	let month = numbers.substring(2, 4);
	let year = numbers.substring(4, 8);

	// Validate day (01-31)
	if (day.length === 2) {
		const dayNum = parseInt(day, 10);
		if (dayNum < 1 || dayNum > 31) {
			day = day.substring(0, 1); // Keep only first digit if invalid
			numbers = numbers.substring(0, 1);
		}
	}

	// Validate month (01-12) - only if day is complete
	if (day.length === 2 && month.length === 2) {
		const monthNum = parseInt(month, 10);
		if (monthNum < 1 || monthNum > 12) {
			month = month.substring(0, 1); // Keep only first digit if invalid
			numbers = numbers.substring(0, 3);
		}
	}

	// Validate year (up to 2100) - only if month is complete
	if (day.length === 2 && month.length === 2 && year.length === 4) {
		const yearNum = parseInt(year, 10);
		if (yearNum > 2100) {
			year = year.substring(0, 3); // Keep first 3 digits if invalid
			numbers = numbers.substring(0, 7);
		}
	}

	// Rebuild numbers string after potential validation corrections
	numbers = day + month + year;

	// Apply mask progressively
	if (numbers.length <= 2) {
		return numbers;
	}

	if (numbers.length <= 4) {
		// Don't add slash if user is deleting
		if (value.length < 3 && value.endsWith('/')) {
			return numbers.substring(0, 2);
		}
		return `${numbers.substring(0, 2)}/${numbers.substring(2, 4)}`;
	}

	// For dates with year
	if (numbers.length <= 8) {
		// Handle backspace after second slash
		if (value.length < 6 && value.endsWith('/')) {
			return `${numbers.substring(0, 2)}/${numbers.substring(2, 4)}`;
		}
		return `${numbers.substring(0, 2)}/${numbers.substring(
			2,
			4,
		)}/${numbers.substring(4, 8)}`;
	}

	// If more than 8 digits, truncate to DD/MM/YYYY format
	return `${numbers.substring(0, 2)}/${numbers.substring(
		2,
		4,
	)}/${numbers.substring(4, 8)}`;
};
